import React from 'react';
import { Trash2 } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';

/**
 * @typedef {{ id: number, code: string, displayName: string }} CompanySource
 *
 * @param {Object} props
 * @param {CompanySource[]} [props.companySourceOptions]
 * @param {boolean} [props.canDelete]
 * @param {string} props.fieldIdPrefix
 * @param {boolean} [props.isFbmMode]
 * @param {number} props.lineIndex
 * @param {{ id: string, companySourceId: number | null, numCases: number, expiration: string, supplierLot: string }} props.line
 * @param {(patch: Partial<typeof props.line>) => void} props.onChange
 * @param {() => void} props.onDelete
 */
function PalletLineItem({
    canDelete = true,
    companySourceOptions = [],
    fieldIdPrefix,
    isFbmMode = false,
    line,
    lineIndex,
    onChange,
    onDelete,
}) {
    const lotLabel = isFbmMode ? 'Partner Lot' : 'Supplier Lot';
    const sourceValue = line.companySourceId !== null ? String(line.companySourceId) : '';

    return (
        <div className="flex items-end gap-2 border-b border-border/60 py-1.5 last:border-b-0">
            <span className="w-5 shrink-0 pb-2 text-xs text-muted-foreground tabular-nums">#{lineIndex + 1}</span>
            {!isFbmMode ? (
                <div className="flex min-w-[120px] flex-[1.2] flex-col gap-1">
                    <Label className="text-xs leading-none text-muted-foreground" htmlFor={`${fieldIdPrefix}-source`}>
                        Source
                    </Label>
                    <Select value={sourceValue} onValueChange={(value) => onChange({ companySourceId: Number(value) })}>
                        <SelectTrigger id={`${fieldIdPrefix}-source`} className="w-full" size="sm">
                            <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                            {companySourceOptions.map((source) => (
                                <SelectItem key={source.id} value={String(source.id)}>
                                    {source.displayName}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            ) : null}
            <div className="flex min-w-[72px] flex-[0.6] flex-col gap-1">
                <Label className="text-xs leading-none text-muted-foreground" htmlFor={`${fieldIdPrefix}-cases`}>
                    Cases
                </Label>
                <Input
                    id={`${fieldIdPrefix}-cases`}
                    className="h-8 text-sm"
                    min={0}
                    type="number"
                    value={line.numCases === 0 ? '' : line.numCases}
                    onChange={(event) => {
                        const raw = event.target.value;
                        if (raw === '') {
                            onChange({ numCases: 0 });
                            return;
                        }
                        const parsed = Number(raw);
                        onChange({ numCases: Number.isFinite(parsed) && parsed >= 0 ? parsed : 0 });
                    }}
                />
            </div>
            <div className="flex min-w-[120px] flex-1 flex-col gap-1">
                <Label className="text-xs leading-none text-muted-foreground" htmlFor={`${fieldIdPrefix}-expiration`}>
                    Expiration
                </Label>
                <Input
                    id={`${fieldIdPrefix}-expiration`}
                    className="h-8 text-sm"
                    type="date"
                    value={line.expiration}
                    onChange={(event) => onChange({ expiration: event.target.value })}
                />
            </div>
            <div className="flex min-w-[100px] flex-1 flex-col gap-1">
                <Label className="text-xs leading-none text-muted-foreground" htmlFor={`${fieldIdPrefix}-lot`}>
                    {lotLabel}
                </Label>
                <Input
                    id={`${fieldIdPrefix}-lot`}
                    className="h-8 text-sm"
                    placeholder="Lot #"
                    value={line.supplierLot}
                    onChange={(event) => onChange({ supplierLot: event.target.value })}
                />
            </div>
            <Button
                aria-label="Delete line"
                className="shrink-0"
                disabled={!canDelete}
                size="iconSm"
                type="button"
                variant="ghost"
                onClick={onDelete}
            >
                <Trash2 className="size-3.5" />
            </Button>
        </div>
    );
}

export { PalletLineItem };
